// Утилиты для работы с Telegram WebApp API
import type { RuntimeMessengerUser } from '@/shared/auth/runtimeAuth';

declare global {
  interface Window {
    __TELEGRAM_EARLY_INITIALIZED?: boolean;
    WebApp?: {
      initData?: string;
      initDataUnsafe?: { user?: RuntimeMessengerUser };
      ready?: () => void;
      expand?: () => void;
      requestFullscreen?: () => void | Promise<void>;
      isFullscreen?: boolean;
      safeAreaInset?: { top?: number };
      contentSafeAreaInset?: { top?: number };
      setBackgroundColor?: (color: string) => void;
      setHeaderColor?: (color: string) => void;
      disableVerticalSwipes?: () => void;
      version?: string;
      platform?: string;
      themeParams?: {
        bg_color?: string;
        text_color?: string;
        hint_color?: string;
        link_color?: string;
        button_color?: string;
        button_text_color?: string;
      };
      onEvent?: (eventType: string, handler: () => void) => void;
      BackButton?: {
        show?: () => void;
        hide?: () => void;
        onClick?: (callback: () => void) => void;
        offClick?: (callback: () => void) => void;
      };
    };
    Telegram?: {
      WebApp?: {
        initData?: string;
        initDataUnsafe?: {
          user?: {
            id?: number;
            first_name?: string;
            last_name?: string;
            username?: string;
            language_code?: string;
            photo_url?: string;
          };
        };
        ready?: () => void;
        expand?: () => void;
        close?: () => void;
        requestFullscreen?: () => void | Promise<void>;
        isFullscreen?: boolean;
        setBackgroundColor?: (color: string) => void;
        setHeaderColor?: (color: string) => void;
        disableVerticalSwipes?: () => void;
        enableVerticalSwipes?: () => void;
        BackButton?: {
          show?: () => void;
          hide?: () => void;
          onClick?: (callback: () => void) => void;
          offClick?: (callback: () => void) => void;
        };
        MainButton?: {
          text?: string;
          color?: string;
          textColor?: string;
          isVisible?: boolean;
          isActive?: boolean;
          isProgressVisible?: boolean;
          setText?: (text: string) => void;
          onClick?: (callback: () => void) => void;
          show?: () => void;
          hide?: () => void;
          enable?: () => void;
          disable?: () => void;
          showProgress?: (leaveActive?: boolean) => void;
          hideProgress?: () => void;
        };
        HapticFeedback?: {
          impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred?: (type: 'error' | 'success' | 'warning') => void;
          selectionChanged?: () => void;
        };
        version?: string;
        platform?: string;
        colorScheme?: 'light' | 'dark';
        safeAreaInset?: { top?: number; right?: number; bottom?: number; left?: number };
        contentSafeAreaInset?: { top?: number; right?: number; bottom?: number; left?: number };
        onEvent?: (eventType: string, handler: () => void) => void;
        offEvent?: (eventType: string, handler: () => void) => void;
        themeParams?: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          link_color?: string;
          button_color?: string;
          button_text_color?: string;
          secondary_bg_color?: string;
        };
      };
    };
  }
}

export const isTelegramMiniApp = (): boolean => {
  return typeof window !== 'undefined' && window.Telegram?.WebApp !== undefined;
};

const getMessengerWebApp = () => window.Telegram?.WebApp || window.WebApp;

let surfaceColorObserver: MutationObserver | null = null;
let surfaceColorFrame = 0;
let lastSurfaceColor = '';

const pageSurfaceColor = (): string => {
  const page = document.querySelector?.('[data-telegram-page-surface]')
    || document.querySelector?.('.client-portal-shell')
    || document.querySelector?.('.showcase-catalog:not(.is-background)')
    || document.querySelector?.('main[data-first-screen-ready]')
    || document.querySelector?.('.menu-app');
  const background = page && typeof getComputedStyle === 'function' ? getComputedStyle(page).backgroundColor : '';
  const rgb = background.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (rgb) return `#${rgb.slice(1).map((part) => Number(part).toString(16).padStart(2, '0')).join('')}`;
  try {
    return (typeof getComputedStyle === 'function'
      ? getComputedStyle(document.documentElement).getPropertyValue('--surface-page').trim()
      : '') || '#0d1f2d';
  } catch {
    return '#0d1f2d';
  }
};

const syncPageSurfaceColor = (): void => {
  const color = pageSurfaceColor();
  document.documentElement.style.setProperty('--telegram-page-background', color);
  const webApp = getMessengerWebApp();
  if (!webApp || color === lastSurfaceColor) return;
  try {
    webApp.setBackgroundColor?.(color);
    webApp.setHeaderColor?.(color);
    lastSurfaceColor = color;
  } catch {
    // Older messenger clients may not support native color changes.
  }
};

const observePageSurfaceColor = (): void => {
  syncPageSurfaceColor();
  if (surfaceColorObserver || typeof MutationObserver === 'undefined') return;
  const scheduleSync = () => {
    if (surfaceColorFrame) return;
    surfaceColorFrame = requestAnimationFrame(() => {
      surfaceColorFrame = 0;
      syncPageSurfaceColor();
    });
  };
  surfaceColorObserver = new MutationObserver(scheduleSync);
  surfaceColorObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  const root = document.getElementById?.('root');
  if (root) surfaceColorObserver.observe(root, {
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
    subtree: true,
  });
};

const isTelegramVersionAtLeast = (required: string): boolean => {
  const current = String(getMessengerWebApp()?.version || '0')
    .split('.')
    .map((part) => Number(part) || 0);
  const target = required.split('.').map((part) => Number(part) || 0);
  const length = Math.max(current.length, target.length);
  for (let index = 0; index < length; index += 1) {
    const actual = current[index] || 0;
    const expected = target[index] || 0;
    if (actual !== expected) return actual > expected;
  }
  return true;
};

export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const platform = String(getMessengerWebApp()?.platform ?? '').toLowerCase();
  if (['desktop', 'tdesktop', 'macos', 'windows', 'web', 'weba', 'webk'].includes(platform)) return false;
  if (['android', 'android_x', 'ios'].includes(platform)) return true;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Native device chrome and Telegram content controls have independent insets.
export const getTelegramTopOffset = (runtime: {
  platform?: string; isFullscreen?: boolean;
  safeAreaInset?: { top?: number }; contentSafeAreaInset?: { top?: number };
}, userAgent = ''): string => {
  const platform = String(runtime.platform || '').toLowerCase();
  const desktop = ['desktop', 'tdesktop', 'macos', 'windows', 'web', 'weba', 'webk'].includes(platform);
  const mobile = !desktop && (['android', 'android_x', 'ios'].includes(platform)
    || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent));
  if (!mobile || !runtime.isFullscreen) return '0px';
  const inset = (value?: number) => Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
  return `calc(max(env(safe-area-inset-top, 0px), ${inset(runtime.safeAreaInset?.top)}px) + ${inset(runtime.contentSafeAreaInset?.top) || 56}px)`;
};

export const initTelegramMiniApp = (): void => {
  const runtime = typeof window !== 'undefined' ? getMessengerWebApp() : undefined;
  if (runtime) {
    const tg = runtime;
    const provider = isTelegramMiniApp() ? 'telegram' : 'max';
    const applyTopSafeOffset = (): void => {
      const offset = getTelegramTopOffset(tg, typeof navigator === 'undefined' ? '' : navigator.userAgent);
      document.documentElement.style.setProperty('--telegram-fullscreen-top-offset', offset);
      document.documentElement.style.setProperty('--telegram-top-safe-offset', isMobileDevice() ? 'var(--miniapp-page-top-offset)' : '0px');
    };

    applyTopSafeOffset();
    observePageSurfaceColor();
    if (document.documentElement.dataset.messengerInitialized === provider) return;
    document.documentElement.dataset.messengerInitialized = provider;
    const isMobile = isMobileDevice();
    const wasInitializedEarly = provider === 'telegram' && window.__TELEGRAM_EARLY_INITIALIZED === true;
    
    // Готовим приложение
    if (!wasInitializedEarly) tg.ready?.();
    
    // Управление мобильным viewport не должно менять размер окна Telegram Desktop/Web.
    if (isMobile && !wasInitializedEarly) {
      tg.expand?.();

      // Отключаем pull-to-refresh (стягивание вниз) только на мобильных устройствах.
      if (isTelegramVersionAtLeast('7.7')) {
        tg.disableVerticalSwipes?.();
      }

      if (isTelegramVersionAtLeast('8.0') && !tg.isFullscreen) {
        try {
          const result = tg.requestFullscreen?.();
          applyTopSafeOffset();
          result?.then?.(applyTopSafeOffset).catch?.(() => tg.expand?.());
        } catch {
          tg.expand?.();
        }
      }
    }
    
    // Применяем тему Telegram
    if (tg.themeParams) {
      document.documentElement.style.setProperty('--tg-theme-bg-color', tg.themeParams.bg_color || '#ffffff');
      document.documentElement.style.setProperty('--tg-theme-text-color', tg.themeParams.text_color || '#000000');
      document.documentElement.style.setProperty('--tg-theme-hint-color', tg.themeParams.hint_color || '#999999');
      document.documentElement.style.setProperty('--tg-theme-link-color', tg.themeParams.link_color || '#2481cc');
      document.documentElement.style.setProperty('--tg-theme-button-color', tg.themeParams.button_color || '#2481cc');
      document.documentElement.style.setProperty('--tg-theme-button-text-color', tg.themeParams.button_text_color || '#ffffff');
    }
    
    // Добавляем класс для стилизации
    document.documentElement.classList.add('telegram-miniapp');
    document.documentElement.classList.add(`${provider}-miniapp`);
    document.documentElement.dataset.deviceClass = isMobile ? 'mobile' : 'desktop';
    
    // Если это мобильное устройство, добавляем дополнительный класс
    if (isMobile) {
      document.documentElement.classList.add('telegram-miniapp-mobile');
    } else {
      document.documentElement.classList.add('telegram-miniapp-desktop');
    }

    const syncViewport = () => { applyTopSafeOffset(); syncPageSurfaceColor(); };
    ['fullscreenChanged', 'safeAreaChanged', 'contentSafeAreaChanged', 'viewportChanged', 'activated']
      .forEach((event) => tg.onEvent?.(event, syncViewport));
    window.addEventListener('resize', applyTopSafeOffset, { passive: true });
    
  }
};

export const setupTelegramBackButton = (onBack?: () => void): (() => void) => {
  const telegramBackButton = isTelegramMiniApp() && isTelegramVersionAtLeast('6.1')
    ? window.Telegram?.WebApp?.BackButton
    : undefined;
  const backButton = telegramBackButton || window.WebApp?.BackButton;
  if (backButton) {
    
    // Очищаем предыдущие обработчики, если они есть
    const existingPopStateHandler = (window as any).__telegramBackButtonHandler;
    if (existingPopStateHandler) {
      window.removeEventListener('popstate', existingPopStateHandler);
    }
    const existingClickHandler = (window as any).__telegramBackButtonClickHandler;
    if (existingClickHandler) {
      backButton?.offClick?.(existingClickHandler);
    }
    
    // Восстанавливаем оригинальные методы, если они были переопределены
    const originalPushState = (window as any).__telegramPushStateHandler || history.pushState;
    const originalReplaceState = (window as any).__telegramReplaceStateHandler || history.replaceState;
    
    if ((window as any).__telegramPushStateWrapped) {
      history.pushState = originalPushState;
    }
    if ((window as any).__telegramReplaceStateWrapped) {
      history.replaceState = originalReplaceState;
    }
    
    // Функция для проверки истории и обновления состояния кнопки
    let active = true;
    const updateBackButton = () => {
      if (!active) return;
      if (backButton) {
        // Проверяем текущий путь
        const path = window.location.pathname;
        const isInitialPage = ['/', '/show-case', '/manager', '/specialist'].includes(path);
        
        // На странице /show-case скрываем кнопку "Назад"
        if (isInitialPage) {
          backButton.hide?.();
        } else {
          // На других страницах проверяем, можем ли мы вернуться назад
          const canGoBack = Boolean(onBack) || window.history.length > 1;
          
          if (canGoBack) {
            // Если есть история, показываем кнопку "Назад"
            backButton.show?.();
          } else {
            // Если нет истории, скрываем кнопку "Назад"
            backButton.hide?.();
          }
        }
      }
    };
    
    // Инициализируем состояние кнопки
    updateBackButton();
    
    let registeredClickHandler: (() => void) | undefined;
    if (backButton) {
      const handleBackClick = () => {
        if (onBack) {
          onBack();
          return;
        }
        // Проверяем историю перед навигацией
        if (window.history.length > 1) {
          // Пытаемся вернуться назад
          window.history.back();
          
          // После навигации проверяем, осталась ли история
          // Используем небольшую задержку, чтобы дать время истории обновиться
          setTimeout(() => {
            updateBackButton();
          }, 50);
        } else {
          // Если истории нет, обновляем состояние кнопки
          updateBackButton();
        }
      };
      backButton.onClick?.(handleBackClick);
      registeredClickHandler = handleBackClick;
      (window as any).__telegramBackButtonClickHandler = handleBackClick;
    }
    
    // Отслеживаем изменения истории через popstate
    const handlePopState = () => {
      updateBackButton();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // Отслеживаем изменения пути через pushstate/replacestate
    const wrappedPushState = function(...args: any[]) {
      originalPushState.apply(history, args);
      setTimeout(() => updateBackButton(), 0);
    };
    
    const wrappedReplaceState = function(...args: any[]) {
      originalReplaceState.apply(history, args);
      setTimeout(() => updateBackButton(), 0);
    };
    
    history.pushState = wrappedPushState;
    history.replaceState = wrappedReplaceState;
    
    // Сохраняем обработчики для возможной очистки
    (window as any).__telegramBackButtonHandler = handlePopState;
    (window as any).__telegramPushStateHandler = originalPushState;
    (window as any).__telegramReplaceStateHandler = originalReplaceState;
    (window as any).__telegramPushStateWrapped = true;
    (window as any).__telegramReplaceStateWrapped = true;
    return () => {
      active = false;
      window.removeEventListener('popstate', handlePopState);
      if (registeredClickHandler) {
        backButton?.offClick?.(registeredClickHandler);
      }
      if ((window as any).__telegramBackButtonClickHandler === registeredClickHandler) {
        delete (window as any).__telegramBackButtonClickHandler;
        delete (window as any).__telegramBackButtonHandler;
        if (history.pushState === wrappedPushState) history.pushState = originalPushState;
        if (history.replaceState === wrappedReplaceState) history.replaceState = originalReplaceState;
        delete (window as any).__telegramPushStateWrapped;
        delete (window as any).__telegramReplaceStateWrapped;
        backButton.hide?.();
      }
    };
  }
  return () => undefined;
};

export const hideTelegramBackButton = (): void => {
  const telegramBackButton = isTelegramMiniApp() && isTelegramVersionAtLeast('6.1')
    ? window.Telegram?.WebApp?.BackButton
    : undefined;
  (telegramBackButton || window.WebApp?.BackButton)?.hide?.();
};

export const getTelegramUser = () => {
  if (isTelegramMiniApp()) {
    return window.Telegram!.WebApp!.initDataUnsafe?.user;
  }
  return null;
};
